import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Table, Select, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { FormContext } from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import useDictionaries from '../../hooks/useDictionaries';
import { getProducts } from '../../services/offers/products';

const now = new Date();

const dictionaries = {
  products: () =>
    getProducts({ criterias: { categoryName: 'PURSE', page: 0, size: 10000 } }),
};

const RetailerPosLimitsList = ({ canEdit = true }) => {
  const form = useContext(FormContext);
  const [t] = useTranslation();

  const { value, setValue, custom, select, checkbox, input } = form;

  const { routePrices } = value;

  const onAdd = useCallback(
    () => setValue('routePrices', [...routePrices, { key: Math.random() }]),
    [setValue, routePrices],
  );

  // const onRemove = useCallback(
  //   async (record) =>
  //     setValue(
  //       'routePrices',
  //       routePrices.filter((routePrice) =>
  //         record.key
  //           ? routePrice.key !== record.key
  //           : routePrice.id !== record.id,
  //       ),
  //     ),
  //   [routePrices, setValue],
  // );

  const [{ products }] = useDictionaries(dictionaries);

  const columns = useMemo(
    () => [
      {
        title: t('entity.offers.product.active'),
        dataIndex: 'active',
        key: 'active',
        width: 200,
        render: (_, record, index) => (
          <FormWrapper.CheckBox
            props={{
              ...checkbox(`routePrices[${index}].active`),
              disabled: !canEdit,
            }}
          />
        ),
      },
      {
        title: t('entity.offers.product.label'),
        dataIndex: 'label',
        key: 'label',
        width: 200,
        render: (_, record, index) => (
          <FormWrapper.TextArea
            // label={t('entity.offers.product.label')}
            props={{
              ...input(`routePrices[${index}].label`),
              disabled: !canEdit,
              maxLength: 100,
            }}
          />
        ),
      },
      {
        title: t('entity.offers.product._singular'),
        dataIndex: 'product',
        key: 'product',
        width: 200,
        render: (_, record, index) => (
          <FormWrapper.Select
            props={{
              size: 'small',
              options: products.content,
              loading: products.loading,
              ...select(`routePrices[${index}].productId`),
              disabled: !canEdit,
            }}
          />
        ),
      },
      {
        title: t('entity.taxonomy.route.dateStart'),
        dataIndex: 'dateStart',
        key: 'dateStart',
        width: 200,
        render: (_, record, index) => {
          return (
            <FormWrapper.DateTimePicker
              props={{
                ...custom(`routePrices[${index}].dateStart`),
                size: 'small',
                disabledDate: (date) => date.isSameOrBefore(now),
                disabled: !canEdit,
              }}
            />
          );
        },
      },
      {
        title: t('entity.offers.product.validatorLabel'),
        dataIndex: 'validatorLabel',
        key: 'validatorLabel',
        width: 200,
        render: (_, record, index) => (
          <FormWrapper.Input
            props={{
              ...input(`routePrices[${index}].validatorLabel`),
              disabled: !canEdit,
            }}
          />
        ),
      },
      {
        title: t('entity.offers.product.groupIdsThatCanPay'),
        dataIndex: 'groupIdsThatCanPay',
        key: 'groupIdsThatCanPay',
        width: 250,
        render: (_unused, _record, index) => {
          return (
            <Tooltip
              title={t('entity.offers.product.groupIdsThatCanPayTooltip')}
              placement="topLeft"
              trigger={['focus', 'click']}
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                value={
                  value.routePrices?.[index]?.groupIdsThatCanPay?.map(String) ||
                  []
                }
                onChange={(vals) => {
                  // keep only numeric entries, convert back to Number[]
                  const ids = vals
                    .map((v) => v.trim())
                    .filter((v) => /^\d+$/.test(v))
                    .map(Number);
                  setValue(`routePrices[${index}].groupIdsThatCanPay`, ids);
                }}
                disabled={!canEdit}
                placeholder={t('entity.offers.product.groupIdsThatCanPay')}
              />
            </Tooltip>
          );
        },
      },

      // Column.actions(
      //   t('table.actions'),
      //   (limit) =>
      //     !limit.disabled && (
      //       <DeleteItemIcon
      //         title={t('components.limits._delete')}
      //         item={limit}
      //         action={onRemove}
      //         reload={() => null}
      //       />
      //     ),
      // ),
    ],
    [
      canEdit,
      custom,
      products.content,
      products.loading,
      select,
      t,
      checkbox,
      input,
      setValue,
      value.routePrices,
    ],
  );

  return (
    <>
      {routePrices.length >= 3 ? (
        <Alert
          style={{ marginBottom: '10px' }}
          message={t('entity.taxonomy.routeGroup.routePriceLengthInfo')}
          type="info"
          showIcon
        />
      ) : (
        <div>
          <Button
            type="primary"
            style={{ marginBottom: '10px' }}
            icon={<PlusOutlined />}
            onClick={onAdd}
          >
            {' '}
            {t('actions.add')}{' '}
          </Button>
        </div>
      )}
      <Table
        rowKey={(record) => record.key || record.id}
        columns={columns}
        pagination={false}
        dataSource={routePrices}
        size="small"
      />
    </>
  );
};

export default RetailerPosLimitsList;
